import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { COMPANY, LOGO_URL, fallbackContact, telHref, mailHref } from "@/lib/company";

export const Footer = () => {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const year = new Date().getFullYear();
  const services = lang === "de" && content?.services ? content.services : t.services;
  const contact = content?.contact ?? fallbackContact;

  return (
    <footer className="border-t border-[#E5E5E5] bg-[#0A0A0A] text-white" data-testid="footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-4 lg:px-10">
        <div className="lg:col-span-1">
          <div className="inline-block bg-white px-5 py-4">
            <img src={LOGO_URL} alt="Elvocom AG" className="h-14 w-auto object-contain" />
          </div>
          <p className="mt-6 text-sm leading-relaxed text-white/60">
            {t.footer.tagline}
          </p>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-white/40">
            {t.footer.nav}
          </h4>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link to="/" className="text-white/80 transition-colors hover:text-[#E8620E]">{t.nav.home}</Link></li>
            <li><Link to="/leistungen" className="text-white/80 transition-colors hover:text-[#E8620E]">{t.nav.services}</Link></li>
            <li><Link to="/ueber-uns" className="text-white/80 transition-colors hover:text-[#E8620E]">{t.nav.about}</Link></li>
            <li><Link to="/team" className="text-white/80 transition-colors hover:text-[#E8620E]">{t.nav.team}</Link></li>
            <li><Link to="/kontakt" className="text-white/80 transition-colors hover:text-[#E8620E]">{t.nav.contact}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-white/40">
            {t.footer.services}
          </h4>
          <ul className="mt-5 space-y-3 text-sm text-white/80">
            {services.map((s) => (
              <li key={s.key}>{s.title}</li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs uppercase tracking-[0.2em] text-white/40">
            {t.footer.contact}
          </h4>
          <ul className="mt-5 space-y-4 text-sm text-white/80">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="mt-0.5 shrink-0 text-[#E8620E]" />
              <span>{contact.street}<br />{contact.city}</span>
            </li>
            <li>
              <a href={telHref(contact.phone)} className="flex items-center gap-3 transition-colors hover:text-[#E8620E]">
                <Phone size={18} className="shrink-0 text-[#E8620E]" /> {contact.phone}
              </a>
            </li>
            <li>
              <a href={mailHref(contact.email)} className="flex items-center gap-3 transition-colors hover:text-[#E8620E]">
                <Mail size={18} className="shrink-0 text-[#E8620E]" /> {contact.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Globe size={18} className="shrink-0 text-[#E8620E]" /> {contact.website}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-6 py-6 text-xs text-white/40 sm:flex-row lg:px-10">
          <span>© {year} {COMPANY.name}. {t.footer.rights}</span>
          <div className="flex items-center gap-5">
            <Link to="/impressum" data-testid="footer-impressum-link" className="transition-colors hover:text-white">{t.footer.impressum}</Link>
            <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="transition-colors hover:text-white">{t.footer.datenschutz}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
